import {Button} from "primereact/button";
import {useSelector} from "react-redux";

import {useNavigateWithQuery} from "../../hooks/navigationHooks";
import {
    type AppreciationKey,
    getAppreciationId,
    selectAppreciationValueOrDefaultCurried,
} from "../../redux/appreciationsUtil";
import {selectReadOnly} from "../../redux/persistenceSlice";
import {resolveAppreciationId, useAppreciationVisible} from "./appreciationHooks";

interface AppreciationToolbarButtonProps {
    /** option1: provide appreciation key */
    appreciationKey?: AppreciationKey;
    /** option2: provide appreciation page and tab, to calculate key */
    page?: string;
    tab?: string;
    appreciationVisible: boolean;
    setAppreciationVisible: (visible: boolean) => void;
    /** if given, clicking navigates here (query string preserved) instead of toggling */
    linkTo?: string;
    /** optional classname to define the margin of the button (default: no margin) */
    classNameMargin?: string;
}

/**
 * React component for an Appreciation Toggle Button in the toolbar of a page/tab. With `linkTo` given,
 * it navigates there instead of toggling an inline edit box -- for appreciations edited elsewhere
 */
export default function AppreciationToolbarButton({
    appreciationKey,
    page,
    tab,
    appreciationVisible,
    setAppreciationVisible,
    linkTo,
    classNameMargin = "m-0",
}: AppreciationToolbarButtonProps) {
    const effectiveAppreciationKey = resolveAppreciationId(appreciationKey, page, tab);
    const storedValue = useSelector(selectAppreciationValueOrDefaultCurried(effectiveAppreciationKey));
    const readOnly = useSelector(selectReadOnly);
    const appreciationId = getAppreciationId(effectiveAppreciationKey);
    const appreciationAllowed = useAppreciationVisible(effectiveAppreciationKey);
    const navigate = useNavigateWithQuery();

    if (!appreciationAllowed) {
        // Appreciation UI requires permission, except for the always-visible exception fields (#18607)
        return null;
    }

    const onButtonClick = () => {
        if (linkTo) {
            navigate(linkTo);
            return;
        }
        if (readOnly) {
            return;
        }
        setAppreciationVisible(!appreciationVisible);
    };

    const icon = "pi pi-file-edit";
    // Linking is a read-only navigation, not an edit action -- stays enabled/clickable in readOnly.
    const disabled = linkTo ? false : readOnly;
    const previewTooltip = `Würdigung: ${storedValue || "keine Würdigung hinterlegt."}`;
    const tooltip = linkTo
        ? previewTooltip
        : readOnly
          ? undefined
          : appreciationVisible
            ? "Würdigung ausblenden"
            : previewTooltip;

    return (
        <Button
            id={`appreciationButton_${appreciationId}`}
            className={`${classNameMargin} pl-0`}
            icon={icon}
            text
            disabled={disabled}
            severity={!linkTo && (appreciationVisible || readOnly) ? "secondary" : undefined}
            tooltip={tooltip}
            tooltipOptions={{position: "left", className: "appreciation-tooltip"}}
            onClick={onButtonClick}
        />
    );
}
